import React, { useEffect, useState } from 'react';
import api from '../../api';
import AdminSectionHeader from '../../components/admin/AdminSectionHeader';
import StatusMessage from '../../components/admin/StatusMessage';
import RoleBadge from '../../components/admin/RoleBadge';
import ConfirmationModal from '../../components/admin/ConfirmationModal';

export default function MembersPage() {
  const [members, setMembers] = useState([]);
  const [memberEdit, setMemberEdit] = useState({ id: null, full_name: '', subscribe_news: false });
  const [msg, setMsg] = useState({ type: '', text: '' });
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    loadMembers();
  }, []);

  const loadMembers = () => {
    api.get('/api/admin/members')
      .then((r) => setMembers(r.data || []))
      .catch(() => setMsg({ type: 'error', text: 'Failed to load members' }));
  };

  const showMsg = (type, text, duration = 3000) => {
    setMsg({ type, text });
    setTimeout(() => setMsg({ type: '', text: '' }), duration);
  };

  const startEdit = (m) => {
    setMemberEdit({ id: m.id, full_name: m.full_name, subscribe_news: m.subscribe_news });
  };

  const saveMember = async () => {
    if (!memberEdit.id) return;
    try {
      await api.patch(`/api/admin/members/${memberEdit.id}`, memberEdit);
      showMsg('success', 'Member updated successfully');
      loadMembers();
      setMemberEdit({ id: null, full_name: '', subscribe_news: false });
    } catch (e) {
      showMsg('error', `Failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/api/admin/members/${deleteTarget.id}`);
      showMsg('success', 'Member deleted');
      loadMembers();
    } catch (e) {
      showMsg('error', `Failed: ${e.response?.data?.detail || e.message}`);
    }
    setDeleteTarget(null);
  };

  const toggleAdmin = async (id, currentRoles) => {
    try {
      await api.post(`/api/admin/members/${id}/role`, { make_admin: !currentRoles.includes('admin') });
      showMsg('success', 'Role updated');
      loadMembers();
    } catch (e) {
      showMsg('error', `Failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  const setActive = async (id, isActive) => {
    try {
      await api.patch(`/api/admin/members/${id}`, { is_active: !isActive });
      showMsg('success', 'Account status updated');
      loadMembers();
    } catch (e) {
      showMsg('error', `Failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  const resetPassword = async (id) => {
    try {
      await api.post(`/api/admin/members/${id}/reset-password`);
      showMsg('success', 'Reset email sent to member');
    } catch (e) {
      showMsg('error', `Failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  const reEnableMember = async (id) => {
    try {
      await api.post(`/api/admin/members/${id}/re-enable`);
      showMsg('success', 'Member re-enabled');
      loadMembers();
    } catch (e) {
      showMsg('error', `Failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  return (
    <div className="admin-members-page">
      <AdminSectionHeader
        title="Manage Members"
        subtitle="View and manage member accounts, roles, and permissions"
      />

      {msg.text && (
        <StatusMessage type={msg.type} message={msg.text} />
      )}

      <div className="members-list">
        {members.length === 0 ? (
          <p className="empty-state">No members found.</p>
        ) : (
          <div className="members-grid">
            {members.map((m) => (
              <div key={m.id} className="member-card">
                <div className="member-info">
                  <div className="member-header">
                    <strong className="member-name">{m.full_name || 'Unknown'}</strong>
                    <RoleBadge roles={m.roles} isActive={m.is_active} />
                  </div>
                  <div className="member-meta">
                    <span className={m.email_verified ? 'verified' : 'unverified'}>
                      {m.email_verified ? '✓ Verified' : '✗ Unverified'}
                    </span>
                    <span className="member-email">{m.email}</span>
                  </div>
                </div>

                <div className="member-actions">
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={() => startEdit(m)}
                    title="Edit member"
                  >
                    <i className="fa-solid fa-pen"></i>
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    onClick={() => toggleAdmin(m.id, m.roles)}
                    title={m.roles?.includes('admin') ? 'Remove Admin' : 'Make Admin'}
                  >
                    <i className="fa-solid fa-shield-halved"></i>
                  </button>
                                    <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    onClick={() => setActive(m.id, m.is_active)}
                    title={m.is_active ? 'Disable' : 'Enable'}
                  >
                    <i className={`fa-solid fa-${m.is_active ? 'ban' : 'check'}`}></i>
                  </button>
                  {m.account_disabled_reason && (
                    <button
                      type="button"
                      className="btn btn-sm btn-success"
                      onClick={() => reEnableMember(m.id)}
                      title="Re-enable account"
                    >
                      <i className="fa-solid fa-unlock"></i>
                    </button>
                  )}
                  <span className="account-disabled-reason" title={m.account_disabled_reason}>
                    {m.account_disabled_reason}
                  </span>
<button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    onClick={() => resetPassword(m.id)}
                    title="Reset password"
                  >
                    <i className="fa-solid fa-key"></i>
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-danger"
                    onClick={() => setDeleteTarget(m)}
                    title="Delete member"
                  >
                    <i className="fa-solid fa-trash"></i>
                  </button>
                </div>

                {/* Inline edit form */}
                {memberEdit.id === m.id && (
                  <div className="member-edit-form">
                    <input
                      type="text"
                      value={memberEdit.full_name}
                      onChange={(e) => setMemberEdit((prev) => ({ ...prev, full_name: e.target.value }))}
                      placeholder="Full name"
                    />
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={memberEdit.subscribe_news}
                        onChange={(e) => setMemberEdit((prev) => ({ ...prev, subscribe_news: e.target.checked }))}
                      />
                      Subscribe to newsletter
                    </label>
                    <div className="edit-actions">
                      <button type="button" className="btn btn-sm" onClick={saveMember}>
                        Save
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-secondary"
                        onClick={() => setMemberEdit({ id: null, full_name: '', subscribe_news: false })}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={!!deleteTarget}
        title="Delete Member"
        message={`Are you sure you want to delete "${deleteTarget?.full_name}"? Their blogs/comments will be preserved.`}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
        variant="danger"
      />
    </div>
  );
}